(() => {
  const DRAG_THRESHOLD = 6;

  function updateControls(track, prevButton, nextButton) {
    if (!track || !prevButton || !nextButton) return;

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    prevButton.disabled = track.scrollLeft <= 2;
    nextButton.disabled = track.scrollLeft >= maxScrollLeft - 2;
  }

  function initializeSection(section) {
    if (!section || section.dataset.categoriesSliderInitialized === 'true') return;

    const track = section.querySelector('[data-slider-track]');
    const prevButton = section.querySelector('[data-slider-prev]');
    const nextButton = section.querySelector('[data-slider-next]');

    if (!track) return;

    section.dataset.categoriesSliderInitialized = 'true';

    const desktopMQ = window.matchMedia('(min-width: 1024px)');
    let isDesktop = desktopMQ.matches;
    let desktopIndex = 0;

    const getSlides = () =>
      Array.from(track.querySelectorAll('.collection-categories-slider__slide'));

    const getStep = () => {
      const slides = getSlides();
      if (!slides.length) return 0;
      if (slides.length === 1) {
        const trackStyle = window.getComputedStyle(track);
        const gap = parseFloat(trackStyle.gap) || 0;
        return slides[0].offsetWidth + gap;
      }
      return slides[1].offsetLeft - slides[0].offsetLeft;
    };

    const computeVisibleCount = () => {
      const slides = getSlides();
      if (!slides.length) return 1;
      const trackStyle = window.getComputedStyle(track);
      const paddingLeft = parseFloat(trackStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(trackStyle.paddingRight) || 0;
      const gap = parseFloat(trackStyle.gap) || 0;
      const innerWidth = track.clientWidth - paddingLeft - paddingRight;
      const step = getStep();
      if (step === 0) return 1;
      return Math.max(1, Math.floor((innerWidth + gap) / step));
    };

    const applyDesktopTransforms = () => {
      const slides = getSlides();
      const step = getStep();

      slides.forEach((slide, i) => {
        if (i < desktopIndex - 1) {
          slide.style.transform = `translateX(${-i * step}px) scale(0)`;
          slide.style.opacity = '0';
          slide.style.zIndex = '0';
          slide.style.pointerEvents = 'none';
          slide.setAttribute('aria-hidden', 'true');
        } else if (i === desktopIndex - 1) {
          slide.style.transform = `translateX(${-i * step}px) scale(0.5)`;
          slide.style.opacity = '1';
          slide.style.zIndex = '1';
          slide.style.pointerEvents = 'none';
          slide.setAttribute('aria-hidden', 'true');
        } else {
          slide.style.transform = `translateX(${-desktopIndex * step}px) scale(1)`;
          slide.style.opacity = '';
          slide.style.zIndex = '2';
          slide.style.pointerEvents = '';
          slide.removeAttribute('aria-hidden');
        }
      });
    };

    const clearDesktopStyles = () => {
      getSlides().forEach((slide) => {
        slide.style.transform = '';
        slide.style.opacity = '';
        slide.style.zIndex = '';
        slide.style.pointerEvents = '';
        slide.removeAttribute('aria-hidden');
      });
    };

    const updateDesktopControls = () => {
      if (!prevButton || !nextButton) return;
      const slides = getSlides();
      const visibleCount = computeVisibleCount();
      const maxIndex = Math.max(0, slides.length - visibleCount);
      prevButton.disabled = desktopIndex <= 0;
      nextButton.disabled = desktopIndex >= maxIndex;
    };

    const scrollPaddingLeft = () => {
      const value = window.getComputedStyle(track).scrollPaddingLeft;
      return Number.parseFloat(value) || 0;
    };

    const slidePositions = () =>
      getSlides().map((slide) => Math.max(0, slide.offsetLeft - scrollPaddingLeft()));

    const activeSlideIndex = (positions) => {
      const currentLeft = track.scrollLeft;
      return positions.reduce((closestIndex, position, index) => {
        const closestDistance = Math.abs(positions[closestIndex] - currentLeft);
        const distance = Math.abs(position - currentLeft);
        return distance < closestDistance ? index : closestIndex;
      }, 0);
    };

    const scrollToSlide = (direction) => {
      if (isDesktop) {
        const slides = getSlides();
        const visibleCount = computeVisibleCount();
        const maxIndex = Math.max(0, slides.length - visibleCount);
        const newIndex = Math.max(0, Math.min(maxIndex, desktopIndex + direction));
        if (newIndex === desktopIndex) return;
        desktopIndex = newIndex;
        applyDesktopTransforms();
        updateDesktopControls();
        return;
      }

      const positions = slidePositions();
      if (!positions.length) return;

      const currentIndex = activeSlideIndex(positions);
      const targetIndex = Math.max(
        0,
        Math.min(positions.length - 1, currentIndex + direction)
      );
      const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
      const targetLeft = Math.min(positions[targetIndex], maxScrollLeft);

      track.scrollTo({ left: targetLeft, behavior: 'smooth' });
    };

    if (prevButton) {
      prevButton.addEventListener('click', () => {
        scrollToSlide(-1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        scrollToSlide(1);
      });
    }

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let hasDragged = false;

    const startDrag = (x) => {
      isDragging = true;
      hasDragged = false;
      startX = x;
      startScrollLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    };

    const moveDrag = (x) => {
      if (!isDragging) return;

      const deltaX = x - startX;
      if (Math.abs(deltaX) > DRAG_THRESHOLD) {
        hasDragged = true;
      }

      const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
      track.scrollLeft = Math.max(0, Math.min(startScrollLeft - deltaX, maxScrollLeft));
    };

    const endDrag = () => {
      if (!isDragging) return;

      isDragging = false;
      track.classList.remove('is-dragging');
      if (hasDragged) {
        track.dataset.preventClick = 'true';
      }
    };

    track.addEventListener('mousedown', (event) => {
      if (isDesktop) return;
      if (event.button !== 0) return;
      startDrag(event.clientX);
    });

    window.addEventListener('mousemove', (event) => {
      if (!isDragging) return;
      if (hasDragged) event.preventDefault();
      moveDrag(event.clientX);
    });

    window.addEventListener('mouseup', endDrag);

    track.addEventListener(
      'touchstart',
      (event) => {
        if (isDesktop) return;
        if (!event.touches || !event.touches.length) return;
        startDrag(event.touches[0].clientX);
      },
      { passive: true }
    );

    window.addEventListener(
      'touchmove',
      (event) => {
        if (!isDragging || !event.touches || !event.touches.length) return;
        const x = event.touches[0].clientX;
        const deltaX = x - startX;
        if (Math.abs(deltaX) > DRAG_THRESHOLD) {
          hasDragged = true;
          event.preventDefault();
        }
        moveDrag(x);
      },
      { passive: false }
    );

    window.addEventListener('touchend', endDrag);
    window.addEventListener('touchcancel', endDrag);

    track.addEventListener('dragstart', (event) => event.preventDefault());

    track.addEventListener(
      'click',
      (event) => {
        if (track.dataset.preventClick === 'true') {
          event.preventDefault();
          event.stopPropagation();
          track.dataset.preventClick = 'false';
        }
      },
      true
    );

    track.addEventListener(
      'scroll',
      () => {
        if (isDesktop) return;
        updateControls(track, prevButton, nextButton);
      },
      { passive: true }
    );

    const handleViewportChange = () => {
      const wasDesktop = isDesktop;
      isDesktop = desktopMQ.matches;

      if (isDesktop) {
        if (!wasDesktop) {
          track.scrollLeft = 0;
          desktopIndex = 0;
        } else {
          const slides = getSlides();
          const visibleCount = computeVisibleCount();
          const maxIndex = Math.max(0, slides.length - visibleCount);
          desktopIndex = Math.min(desktopIndex, maxIndex);
        }
        applyDesktopTransforms();
        updateDesktopControls();
      } else {
        clearDesktopStyles();
        updateControls(track, prevButton, nextButton);
      }
    };

    if (typeof desktopMQ.addEventListener === 'function') {
      desktopMQ.addEventListener('change', handleViewportChange);
    } else if (typeof desktopMQ.addListener === 'function') {
      desktopMQ.addListener(handleViewportChange);
    }
    window.addEventListener('resize', handleViewportChange);

    handleViewportChange();
  }

  function initializeAll() {
    document.querySelectorAll('[data-category-slider-section]').forEach(initializeSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll);
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', initializeAll);
})();
